import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import { conns } from '@cfg/axiosConfig.tsx';

export interface StaffDirectoryEntry {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  username: string;
  email?: string | null;
  is_active?: boolean;
  type?: string | null;
  is_operational?: boolean | null;
  staff_type?: string | null;
  agenda_color?: string | null;
}

export interface ServiceSummary {
  id: number;
  name: string;
}

export interface ServiceStaffAssignment {
  id: number;
  service_id: number;
  staff_user_id: number;
  created_by?: number | null;
  created_at?: string | null;
  staff?: StaffDirectoryEntry | null;
  service?: ServiceSummary | null;
}

const dedupeServiceIds = (serviceIds: readonly number[]): number[] => {
  const unique = new Set<number>();
  for (const id of serviceIds) {
    if (Number.isFinite(id)) {
      const normalized = Math.trunc(id);
      if (normalized > 0) {
        unique.add(normalized);
      }
    }
  }
  return Array.from(unique).sort((a, b) => a - b);
};

const diffAssignments = (
  current: readonly ServiceStaffAssignment[],
  nextServiceIds: readonly number[],
): {
  toAdd: number[];
  toRemove: ServiceStaffAssignment[];
} => {
  const normalizedNext = dedupeServiceIds(nextServiceIds);
  const currentByService = new Map(current.map((assignment) => [assignment.service_id, assignment]));
  const nextSet = new Set(normalizedNext);

  const toAdd = normalizedNext.filter((serviceId) => !currentByService.has(serviceId));
  const toRemove = current.filter((assignment) => !nextSet.has(assignment.service_id));

  return { toAdd, toRemove };
};

export interface ServiceStaffFilters {
  serviceId?: number;
  staffUserId?: number;
}

const listAssignmentsKey = ['erp', 'services', 'staffAssignments'];

export const fetchStaffDirectory = async (): Promise<StaffDirectoryEntry[]> => {
  const { data } = await conns<StaffDirectoryEntry[]>('user:staff_list').get();
  return data;
};

export const fetchServiceStaffAssignments = async (
  filters?: ServiceStaffFilters,
): Promise<ServiceStaffAssignment[]> => {
  const { serviceId, staffUserId } = filters ?? {};
  const { data } = await conns<ServiceStaffAssignment[]>('erp:services:staff').get({
    service_id: serviceId,
    staff_user_id: staffUserId,
  });
  return data;
};

export const assignStaffToService = async (
  serviceId: number,
  staffUserId: number,
): Promise<ServiceStaffAssignment> => {
  const { data } = await conns<ServiceStaffAssignment>(
    'erp:services:staff',
    [serviceId, staffUserId],
  ).put();
  return data;
};

export const removeStaffFromService = async (
  serviceId: number,
  staffUserId: number,
): Promise<void> => {
  await conns('erp:services:staff', [serviceId, staffUserId]).delete();
};

export const useStaffDirectory = (): UseQueryResult<StaffDirectoryEntry[], Error> => {
  return useQuery<StaffDirectoryEntry[], Error>({
    queryKey: ['erp', 'staff', 'directory'],
    queryFn: fetchStaffDirectory,
    staleTime: 60_000,
  });
};

export const useServiceStaffAssignments = (
  filters?: ServiceStaffFilters,
  options?: Partial<UseQueryOptions<ServiceStaffAssignment[], Error, ServiceStaffAssignment[]>>,
): UseQueryResult<ServiceStaffAssignment[], Error> => {
  return useQuery<ServiceStaffAssignment[], Error, ServiceStaffAssignment[]>({
    queryKey: filters ? [...listAssignmentsKey, filters] : listAssignmentsKey,
    queryFn: () => fetchServiceStaffAssignments(filters),
    staleTime: 10_000,
    ...options,
  });
};

export const useAssignStaffToService = (): UseMutationResult<
  ServiceStaffAssignment,
  Error,
  { serviceId: number; staffUserId: number }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, staffUserId }) => assignStaffToService(serviceId, staffUserId),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: listAssignmentsKey });
      void client.invalidateQueries({ queryKey: [...listAssignmentsKey, { serviceId: variables.serviceId }] });
    },
  });
};

export const useRemoveStaffFromService = (): UseMutationResult<
  void,
  Error,
  { serviceId: number; staffUserId: number }
> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, staffUserId }) => removeStaffFromService(serviceId, staffUserId),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: listAssignmentsKey });
      void client.invalidateQueries({ queryKey: [...listAssignmentsKey, { serviceId: variables.serviceId }] });
    },
  });
};

export interface BulkUpdateServiceAssignmentsVariables {
  staffUserId: number;
  nextServiceIds: number[];
  currentAssignments?: ServiceStaffAssignment[];
  serviceSummaries?: ServiceSummary[];
}

export interface BulkUpdateServiceAssignmentsResult {
  added: ServiceStaffAssignment[];
  removed: number[];
  finalAssignments: ServiceStaffAssignment[];
}

interface BulkUpdateServiceAssignmentsContext {
  previousStaffAssignments?: ServiceStaffAssignment[];
}

export const useBulkUpdateServiceAssignments = (): UseMutationResult<
  BulkUpdateServiceAssignmentsResult,
  Error,
  BulkUpdateServiceAssignmentsVariables,
  BulkUpdateServiceAssignmentsContext
> => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({
      staffUserId,
      nextServiceIds,
      currentAssignments = [],
    }): Promise<BulkUpdateServiceAssignmentsResult> => {
      const { toAdd, toRemove } = diffAssignments(currentAssignments, nextServiceIds);

      if (toAdd.length === 0 && toRemove.length === 0) {
        const normalized = dedupeServiceIds(nextServiceIds);
        const preserved = currentAssignments.filter((assignment) => normalized.includes(assignment.service_id));
        return { added: [], removed: [], finalAssignments: preserved };
      }

      if (toRemove.length > 0) {
        await Promise.all(
          toRemove.map((assignment) => removeStaffFromService(assignment.service_id, staffUserId)),
        );
      }

      const addedAssignments: ServiceStaffAssignment[] =
        toAdd.length > 0
          ? await Promise.all(toAdd.map((serviceId) => assignStaffToService(serviceId, staffUserId)))
          : [];

      const nextSet = new Set(dedupeServiceIds(nextServiceIds));
      const finalAssignments = [
        ...currentAssignments.filter((assignment) => nextSet.has(assignment.service_id)),
        ...addedAssignments,
      ].sort((a, b) => a.service_id - b.service_id);

      return {
        added: addedAssignments,
        removed: toRemove.map((assignment) => assignment.service_id),
        finalAssignments,
      };
    },
    onMutate: async (variables) => {
      const staffKey = [...listAssignmentsKey, { staffUserId: variables.staffUserId }];
      const previousStaffAssignments = client.getQueryData<ServiceStaffAssignment[]>(staffKey);
      const currentAssignments = variables.currentAssignments ?? previousStaffAssignments ?? [];
      const { toAdd, toRemove } = diffAssignments(currentAssignments, variables.nextServiceIds);

      if (previousStaffAssignments) {
        const optimisticPlaceholders = toAdd.map((serviceId) => {
          const summary = variables.serviceSummaries?.find((service) => service.id === serviceId) ?? null;
          return {
            id: -serviceId,
            service_id: serviceId,
            staff_user_id: variables.staffUserId,
            created_at: new Date().toISOString(),
            created_by: null,
            service: summary,
            staff: undefined,
          } satisfies ServiceStaffAssignment;
        });

        const optimisticAssignments = [
          ...previousStaffAssignments.filter((assignment) => !toRemove.some((item) => item.id === assignment.id)),
          ...optimisticPlaceholders,
        ].sort((a, b) => a.service_id - b.service_id);

        client.setQueryData(staffKey, optimisticAssignments);
      }

      return { previousStaffAssignments };
    },
    onError: (_error, variables, context) => {
      if (!context?.previousStaffAssignments) {
        return;
      }
      const staffKey = [...listAssignmentsKey, { staffUserId: variables.staffUserId }];
      client.setQueryData(staffKey, context.previousStaffAssignments);
    },
    onSuccess: (result, variables) => {
      const staffKey = [...listAssignmentsKey, { staffUserId: variables.staffUserId }];
      client.setQueryData(staffKey, result.finalAssignments);
      void client.invalidateQueries({ queryKey: listAssignmentsKey });
      if (variables.nextServiceIds.length > 0) {
        for (const serviceId of variables.nextServiceIds) {
          void client.invalidateQueries({ queryKey: [...listAssignmentsKey, { serviceId }] });
        }
      }
    },
  });
};
